<template>
  <div class="bg-white shadow sm:rounded-lg">
    <div class="px-4 py-5 sm:p-6">
      <div class="flex items-center justify-between">
        <h3 class="text-lg font-medium leading-6 text-gray-900">Compte comptable</h3>
        <label class="inline-flex cursor-pointer items-center gap-2">
          <span class="text-sm text-gray-600">Suivi de solde</span>
          <button type="button" role="switch" :aria-checked="withBalance"
                  @click="toggleWithBalance"
                  :class="[withBalance ? 'bg-indigo-600' : 'bg-gray-200', 'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors focus:outline-none']">
            <span :class="[withBalance ? 'translate-x-5' : 'translate-x-0', 'inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition-transform']"/>
          </button>
        </label>
      </div>

      <p v-if="!withBalance" class="mt-3 text-sm text-gray-500">
        Activez le suivi de solde pour tenir ce fournisseur comme un compte comptable (solde d'ouverture par exercice, puis solde courant transaction par transaction).
      </p>

      <div v-else class="mt-4 space-y-4">
        <div class="flex flex-wrap items-end gap-3 rounded-md bg-gray-50 px-4 py-3">
          <div>
            <label class="block text-sm font-medium text-gray-700">Solde d'ouverture ({{ fiscalYearName }})</label>
            <input type="number" step="0.01" v-model.number="openingInput"
                   class="mt-1 w-48 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"/>
          </div>
          <button type="button" @click="saveOpening"
                  class="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
            Enregistrer
          </button>
          <span v-if="saved" class="text-sm text-green-600">Enregistré ✓</span>
        </div>

        <div class="overflow-hidden rounded-lg ring-1 ring-black ring-opacity-5">
          <table class="min-w-full divide-y divide-gray-200 text-sm">
            <thead class="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th class="px-4 py-2 font-medium">Date</th>
                <th class="px-4 py-2 font-medium">Libellé</th>
                <th class="px-4 py-2 text-right font-medium">Débit</th>
                <th class="px-4 py-2 text-right font-medium">Crédit</th>
                <th class="px-4 py-2 text-right font-medium">Solde</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 bg-white">
              <tr class="bg-gray-50/60">
                <td class="px-4 py-2 text-gray-500" colspan="4">Solde d'ouverture</td>
                <td class="px-4 py-2 text-right font-semibold text-gray-900">{{ money(openingBalance) }}</td>
              </tr>
              <tr v-for="entry in entries" :key="entry.type + entry.id" class="hover:bg-gray-50">
                <td class="whitespace-nowrap px-4 py-2 text-gray-500">{{ formatDate(entry.date) }}</td>
                <td class="px-4 py-2">
                  <router-link :to="`/${entry.type}/${entry.id}`" class="inline-flex items-center gap-1.5 text-gray-900 hover:underline">
                    <span :class="entry.type === 'invoice' ? 'text-pink-500' : 'text-indigo-500'" class="text-xs font-medium uppercase">{{ entry.type === 'invoice' ? 'FAC' : 'TX' }}</span>
                    <span class="truncate">{{ entry.label }}</span>
                  </router-link>
                </td>
                <td class="px-4 py-2 text-right text-red-600">{{ entry.amount < 0 ? money(-entry.amount) : '' }}</td>
                <td class="px-4 py-2 text-right text-green-600">{{ entry.amount > 0 ? money(entry.amount) : '' }}</td>
                <td class="px-4 py-2 text-right font-medium" :class="entry.balance < 0 ? 'text-red-600' : 'text-gray-900'">{{ money(entry.balance) }}</td>
              </tr>
              <tr v-if="!entries.length">
                <td class="px-4 py-6 text-center text-gray-400" colspan="5">Aucun mouvement sur cet exercice.</td>
              </tr>
            </tbody>
            <tfoot class="bg-gray-50">
              <tr>
                <td class="px-4 py-2 font-semibold text-gray-900" colspan="4">Solde de clôture</td>
                <td class="px-4 py-2 text-right font-semibold" :class="closingBalance < 0 ? 'text-red-600' : 'text-gray-900'">{{ money(closingBalance) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  name: "CompanyLedger",
  props: {
    company: { type: Object, required: true },
    fiscalYearId: { type: [Number, String], default: null },
  },
  data() {
    return {
      withBalance: false,
      openingBalance: 0,
      openingInput: 0,
      closingBalance: 0,
      entries: [],
      fiscalYearName: "",
      saved: false,
    };
  },
  watch: {
    fiscalYearId() {
      this.loadLedger();
    },
    "company.id"() {
      this.withBalance = !!this.company.with_balance;
      this.loadLedger();
    },
  },
  mounted() {
    this.withBalance = !!this.company.with_balance;
    this.loadLedger();
  },
  methods: {
    money(value) {
      return `${Number(value || 0).toFixed(2)} €`;
    },
    formatDate(value) {
      return value ? moment(value).format("DD/MM/YYYY") : "";
    },
    async client() {
      const token = await this.$auth.getAccessTokenSilently();
      return axios.create({
        baseURL: "https://billing.clafoutis.eu/api",
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    async loadLedger() {
      if (!this.company || !this.company.id || !this.fiscalYearId) return;
      const http = await this.client();
      const { data } = await http.get(`company/${this.company.id}/ledger`, {
        params: { fiscal_year_id: this.fiscalYearId },
      });
      this.withBalance = !!data.with_balance;
      this.openingBalance = Number(data.opening_balance || 0);
      this.openingInput = this.openingBalance;
      this.closingBalance = Number(data.closing_balance || 0);
      this.entries = data.entries || [];
      this.fiscalYearName = data.fiscal_year ? data.fiscal_year.name : "";
    },
    async persist(payload) {
      const http = await this.client();
      await http.post(`company/${this.company.id}/balance`, {
        fiscal_year_id: this.fiscalYearId,
        ...payload,
      });
    },
    async toggleWithBalance() {
      this.withBalance = !this.withBalance;
      await this.persist({ with_balance: this.withBalance });
      this.$emit("updated");
      if (this.withBalance) this.loadLedger();
    },
    async saveOpening() {
      await this.persist({ with_balance: true, opening_balance: this.openingInput || 0 });
      this.saved = true;
      setTimeout(() => (this.saved = false), 2000);
      this.loadLedger();
    },
  },
};
</script>
